import { Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { getLoginNameById } from '@/services/property_api/sysUserController';

const loginNameCache = new Map<string, string>();
const loginNamePending = new Map<string, Promise<string>>();

const fetchLoginName = (id: string): Promise<string> => {
  const cached = loginNameCache.get(id);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }

  const pending = loginNamePending.get(id);
  if (pending) {
    return pending;
  }

  const request = getLoginNameById({ id })
    .then((res) => {
      const name = res?.data || id;
      loginNameCache.set(id, name);
      return name;
    })
    .catch(() => {
      loginNameCache.set(id, id);
      return id;
    })
    .finally(() => {
      loginNamePending.delete(id);
    });

  loginNamePending.set(id, request);
  return request;
};

export type UserLoginNameProps = {
  id?: string | null;
  fallback?: React.ReactNode;
};

/** 根据用户 ID 异步展示登录名（带缓存，不阻塞列表请求） */
const UserLoginName: React.FC<UserLoginNameProps> = ({ id, fallback = '-' }) => {
  const userId = id ? String(id).trim() : '';
  const [name, setName] = useState<string | undefined>(() =>
    userId ? loginNameCache.get(userId) : undefined,
  );
  const [loading, setLoading] = useState(() => Boolean(userId && !loginNameCache.has(userId)));

  useEffect(() => {
    if (!userId) {
      setName(undefined);
      setLoading(false);
      return;
    }

    const cached = loginNameCache.get(userId);
    if (cached !== undefined) {
      setName(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchLoginName(userId).then((loginName) => {
      if (!cancelled) {
        setName(loginName);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId) {
    return <>{fallback}</>;
  }

  if (loading && name === undefined) {
    return <Spin size="small" />;
  }

  return <>{name || userId}</>;
};

export default UserLoginName;
